// src/rms/pricing/taxEngine.js
// ============================================================================
// Tax computation
// ============================================================================
// Pure functions with no I/O, the same contract as rateEngine.js: one
// implementation serves the counter quote, the booking preview and the invoice.
//
// Nothing here knows what a country is. Every jurisdiction is expressed as
// configuration -- an ordered list of rates, each of which either applies to
// the net or compounds on the rates before it. Quebec stopped compounding QST
// on GST in 2013; that is a boolean in the operator's data, not a code change.
//
// ----------------------------------------------------------------------------
// Two modes, and where tax enters the pipeline
// ----------------------------------------------------------------------------
// EXCLUSIVE  base_rate_cents is net. Tax is added.       (US, Canada)
// INCLUSIVE  base_rate_cents IS the gross price the      (EU, UK, AU, NZ,
//            customer sees and pays. Tax is extracted     where inclusive
//            back out of it.                              display is the law)
//
// In BOTH modes the rate engine runs on the number the operator typed and tax
// is applied once, at the end. rateEngine.js does not know tax exists. That is
// deliberate: threading tax through the rule chain would round at every step
// and make rate_snapshot mean two different things depending on the mode.
//
// ----------------------------------------------------------------------------
// Why BigInt
// ----------------------------------------------------------------------------
// Not defensive -- required. Two things break with Number:
//
//   * A compound multiplier is not an integer at ppm scale. QST compounding on
//     GST is 1.05 x 99750 = 104_737.5 ppm. Held at 1e12 it is exact.
//   * The inclusive extraction computes gross x 1e12. Above about 9000 cents
//     that exceeds Number.MAX_SAFE_INTEGER and starts losing pennies silently.
// ============================================================================

/** Multiplier scale. A multiplier of 1.0 is SCALE. */
const SCALE = 10n ** 12n;

/** Rate scale. 100% is PPM. */
const PPM = 1_000_000n;

/**
 * Round a BigInt quotient half away from zero.
 *
 * Math.round sends both -0.5 and +0.5 toward +Infinity, so a discount line and
 * a charge line of the same magnitude would round in opposite directions and a
 * fully-discounted invoice would not come back to zero. Rounding on the
 * magnitude and restoring the sign keeps them symmetric.
 */
function divRound(numerator, denominator) {
  const negative = numerator < 0n !== denominator < 0n;
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  const q = (n * 2n + d) / (d * 2n);
  return negative ? -q : q;
}

/**
 * Order a group's rates the way they must be evaluated: by sequence, then by
 * creation order. Same tiebreak applicableRules() uses in rateEngine.js, so
 * two rates added on the same screen behave predictably.
 */
export function orderRates(rates) {
  return [...(rates || [])]
    .filter((r) => r && r.is_active !== false)
    .sort((a, b) => {
      const seq = (a.sequence ?? 100) - (b.sequence ?? 100);
      if (seq !== 0) return seq;
      return String(a.created_at || "").localeCompare(String(b.created_at || ""));
    });
}

/**
 * Turn an ordered group of rates into multipliers at SCALE.
 *
 * A compounding rate is applied to the net plus the tax of every EARLIER rate
 * in the group -- earlier only, never "all the others", so the answer does not
 * depend on which order you happen to walk the list in.
 *
 * @returns {{components: {rate: object, m: bigint}[], total: bigint}}
 *   total is SCALE + the sum of all multipliers, i.e. gross = net x total.
 */
export function groupMultipliers(rates) {
  const ordered = orderRates(rates);
  const components = [];
  let priorSum = 0n;

  for (const rate of ordered) {
    const basis = rate.compound ? SCALE + priorSum : SCALE;
    const m = (basis * BigInt(rate.rate_ppm || 0)) / PPM;
    components.push({ rate, m });
    priorSum += m;
  }

  return { components, total: SCALE + priorSum };
}

/**
 * Distribute a rounding residual so the parts sum to the whole exactly.
 *
 * Only ever moves the amount by one unit per step, and always to the largest
 * component first, so the distortion lands where it is proportionally smallest.
 */
function assignResidual(amounts, residual) {
  if (!residual || !amounts.length) return amounts;

  const order = amounts
    .map((cents, i) => ({ cents, i }))
    .sort((a, b) => (b.cents === a.cents ? a.i - b.i : b.cents - a.cents))
    .map((x) => x.i);

  const out = [...amounts];
  const step = residual > 0n ? 1n : -1n;
  let left = residual < 0n ? -residual : residual;
  let k = 0;

  while (left > 0n) {
    out[order[k % order.length]] += step;
    left -= 1n;
    k += 1;
  }
  return out;
}

/**
 * Tax ON a net amount. Exclusive mode.
 * @returns {{net: bigint, tax: bigint, gross: bigint, parts: bigint[]}}
 */
export function taxOnNet(netCents, rates) {
  const { components } = groupMultipliers(rates);
  const net = BigInt(netCents);
  const parts = components.map((c) => divRound(net * c.m, SCALE));
  const tax = parts.reduce((s, p) => s + p, 0n);
  return { net, tax, gross: net + tax, parts, components };
}

/**
 * Tax INSIDE a gross amount. Inclusive mode.
 *
 * The customer was quoted the gross and must pay exactly the gross, so any
 * rounding residual is pushed into the tax components rather than allowed to
 * change the advertised price.
 *
 * @returns {{net: bigint, tax: bigint, gross: bigint, parts: bigint[]}}
 */
export function taxInGross(grossCents, rates) {
  const { components, total } = groupMultipliers(rates);
  const gross = BigInt(grossCents);

  if (!components.length) {
    return { net: gross, tax: 0n, gross, parts: [], components };
  }

  const net = divRound(gross * SCALE, total);
  let parts = components.map((c) => divRound(net * c.m, SCALE));
  const sum = parts.reduce((s, p) => s + p, 0n);

  // net + tax must equal the advertised gross to the cent.
  parts = assignResidual(parts, gross - net - sum);
  const tax = parts.reduce((s, p) => s + p, 0n);

  return { net, tax, gross, parts, components };
}

/**
 * Apply tax to a set of invoice lines and produce the snapshot the invoice
 * stores.
 *
 * Lines are bucketed by tax group and tax is rounded ONCE PER (rate, group) on
 * the aggregated base -- not per line, and not once over the whole invoice.
 * Per line, a thirty-line invoice at 8.875% rounds thirty times and drifts.
 * Once overall cannot produce a per-rate breakdown at all, which is exactly
 * what a compliant VAT or GST invoice has to show.
 *
 * @param {object[]} lines   each { amount_cents, tax_group_id, ... }
 * @param {object}   config  { mode, groups: Map|object of id -> {name, rates[]},
 *                             currency, registration }
 * @returns {{lines, subtotal_cents, tax_cents, total_cents, tax_breakdown}}
 */
export function applyTax(lines, config = {}) {
  const inclusive = config.mode === "inclusive";
  const groupsById = config.groups instanceof Map
    ? config.groups
    : new Map(Object.entries(config.groups || {}));

  // Bucket by group. A null group is untaxed and still has to reach the
  // invoice -- it is the "exempt" figure a return asks for.
  const buckets = new Map();
  const exemptLines = [];

  for (const line of lines) {
    const groupId = line.tax_group_id || null;
    if (!groupId || !groupsById.has(groupId)) {
      exemptLines.push(line);
      continue;
    }
    if (!buckets.has(groupId)) buckets.set(groupId, []);
    buckets.get(groupId).push(line);
  }

  const componentsByRate = new Map();
  const byGroup = [];
  const priced = new Map();
  let netTotal = 0n;
  let taxTotal = 0n;

  for (const [groupId, groupLines] of buckets) {
    const group = groupsById.get(groupId);
    const bucketAmount = groupLines.reduce((s, l) => s + BigInt(l.amount_cents || 0), 0n);

    const result = inclusive
      ? taxInGross(bucketAmount, group.rates)
      : taxOnNet(bucketAmount, group.rates);

    netTotal += result.net;
    taxTotal += result.tax;

    byGroup.push({
      group_id: groupId,
      name: group.name,
      net_cents: Number(result.net),
      tax_cents: Number(result.tax),
    });

    // Merge components across groups by rate: an invoice touching two counties
    // that share the state rate must print ONE state line, because that is the
    // figure the return is filed from. by_group keeps the traceability.
    result.components.forEach((c, i) => {
      const id = c.rate.id;
      const prior = componentsByRate.get(id) || {
        rate_id: id,
        name: c.rate.name,
        authority: c.rate.authority || null,
        registration_number: c.rate.registration_number || null,
        // Snapshotted with the rest: turning the flag on next year must not
        // change what an invoice raised today says.
        show_registration: !!c.rate.show_registration_on_invoice,
        rate_ppm: c.rate.rate_ppm,
        compound: !!c.rate.compound,
        sequence: c.rate.sequence ?? 100,
        base_cents: 0,
        tax_cents: 0,
      };
      // The base this rate actually applied to, after compounding -- so the
      // printed breakdown is self-checking rather than merely plausible.
      const base = divRound(result.net * c.m * PPM, SCALE * BigInt(c.rate.rate_ppm || 1));
      prior.base_cents += Number(c.rate.rate_ppm ? base : result.net);
      prior.tax_cents += Number(result.parts[i]);
      componentsByRate.set(id, prior);
    });

    // Allocate the bucket's net and tax back to its lines by largest remainder,
    // so per-line figures sum to the bucket exactly.
    allocateToLines(groupLines, result, inclusive, priced);
  }

  let exemptTotal = 0n;
  for (const line of exemptLines) {
    const amount = BigInt(line.amount_cents || 0);
    exemptTotal += amount;
    netTotal += amount;
    priced.set(line, { net_cents: Number(amount), tax_cents: 0 });
  }

  const outLines = lines.map((line) => {
    const p = priced.get(line) || { net_cents: line.amount_cents || 0, tax_cents: 0 };
    return { ...line, net_cents: p.net_cents, tax_cents: p.tax_cents };
  });

  const subtotal = Number(netTotal);
  const tax = Number(taxTotal);
  const total = subtotal + tax;

  // In inclusive mode the customer was quoted the sum of the line amounts and
  // must pay precisely that. If this ever fails, a rounding rule changed and
  // every invoice raised since is suspect -- so it throws rather than warns.
  if (inclusive) {
    const quoted = lines.reduce((s, l) => s + (l.amount_cents || 0), 0);
    if (total !== quoted) {
      throw new Error(
        `Tax rounding broke an inclusive-price invoice: quoted ${quoted}, computed ${total}.`
      );
    }
  }

  const components = [...componentsByRate.values()].sort(
    (a, b) => a.sequence - b.sequence || a.name.localeCompare(b.name)
  );

  return {
    lines: outLines,
    subtotal_cents: subtotal,
    tax_cents: tax,
    total_cents: total,
    tax_breakdown: {
      version: 1,
      mode: inclusive ? "inclusive" : "exclusive",
      currency: config.currency || "USD",
      computed_at: config.computedAt || new Date().toISOString(),
      operator_registration: config.registration || null,
      components,
      by_group: byGroup,
      totals: {
        net_cents: subtotal,
        tax_cents: tax,
        gross_cents: total,
        exempt_cents: Number(exemptTotal),
      },
      ...(components.length ? {} : { note: "no_tax_configured" }),
    },
  };
}

/** Largest-remainder allocation of a bucket's net and tax back onto its lines. */
function allocateToLines(groupLines, result, inclusive, out) {
  const amounts = groupLines.map((l) => BigInt(l.amount_cents || 0));
  const totalAmount = amounts.reduce((s, a) => s + a, 0n);

  const share = (target) => {
    if (totalAmount === 0n) return amounts.map(() => 0n);
    const raw = amounts.map((a) => divRound(a * target, totalAmount));
    const drift = target - raw.reduce((s, r) => s + r, 0n);
    return assignResidual(raw, drift);
  };

  // In exclusive mode the line amount already IS the net, so only tax is
  // allocated; splitting the net would only reintroduce rounding error.
  const nets = inclusive ? share(result.net) : amounts;
  const taxes = share(result.tax);

  groupLines.forEach((line, i) => {
    out.set(line, { net_cents: Number(nets[i]), tax_cents: Number(taxes[i]) });
  });
}

// ----------------------------------------------------------------------------
// Display helpers
// ----------------------------------------------------------------------------

/** "9.975" -> 99750. Returns null for anything that is not a usable rate. */
export function parseRatePercent(input) {
  if (input === null || input === undefined || String(input).trim() === "") return null;
  const value = Number(String(input).replace(",", "."));
  if (!Number.isFinite(value) || value < 0 || value > 100) return null;
  return Math.round(value * 10000);
}

/** 99750 -> "9.975". Trailing zeros trimmed, so 20% reads "20" not "20.000". */
export function formatRatePercent(ratePpm) {
  const pct = (Number(ratePpm) || 0) / 10000;
  return String(Number(pct.toFixed(4)));
}

export default { groupMultipliers, taxOnNet, taxInGross, applyTax, orderRates };
